import React, { FC, ReactElement, useState } from 'react';

import styled, { css } from 'styled-components';

import CustomIcon, { SeturIcon } from '@components/utils/CustomIcon/index';

export interface ITabItemProps {
  title: string;
  icon: SeturIcon;
  disabled?: boolean;
}
export interface ITabProps {
  onTabSelection: (selection: number) => void;
  selected: number;
  children: React.ReactNode[];
}
const StyledTabs = styled.div`
  display: flex;
`;

const StyledTab = styled.button<{
  selected?: boolean;
  hover: boolean;
  disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  width: unset;
  min-width: unset;
  flex: 1 1 0px;
  background-color: ${({ theme }) => theme.palette.lynxWhite};
  padding: 20px 0;
  justify-content: center;
  color: ${({ selected, theme }) =>
    selected ? theme.palette.primaryBrand : theme.palette.primaryBrand};
  font-size: 16px;
  font-weight: ${(props) => (props.selected ? 'bold' : 'normal')};
  border: none;
  border-bottom: solid 4px
    ${({ selected, theme }) =>
      selected ? theme.palette.primaryBrand : theme.palette.lightBlueGrey};

  ${(p) =>
    p.disabled
      ? css`
          background: ${({ theme }) => theme.palette.lightBlueGrey};
          color: ${({ theme }) => theme.palette.lightGray};
          pointer-events: none;
        `
      : null}
  ${(p) =>
    p.selected
      ? css`
          background: ${({ theme }) => theme.palette.white};
          &:after {
            border-right: solid 7px transparent;
            border-left: solid 7px transparent;
            border-top: solid 7px ${({ theme }) => theme.palette.primaryBrand};
            transform: translateX(-50%);
            position: absolute;
            content: '';
            bottom: -15%;
            left: 50%;
            height: 0;
            width: 0;
            z-index: 5;
          }
        `
      : null}
`;

const TabText = styled.span`
  padding-left: 5px;
  padding-bottom: 5px;
`;

export const TabItem: FC<{
  onClick?: () => void;
  isSelected?: boolean;
  text: string;
  icon: SeturIcon;
  disabled?: boolean;
  children?: React.ReactNode;
}> = ({ isSelected, text, icon, onClick, disabled }) => {
  const [hover, setHover] = useState(false);
  return (
    <StyledTab
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      selected={isSelected}
      hover={hover}
      onClick={onClick}
      disabled={disabled}
    >
      {isSelected || hover ? (
        <CustomIcon
          paletteColor={isSelected ? 'primaryBrand' : 'brownishGrey'}
          fontSize={20}
          width={20}
          height={20}
          icon={icon}
        />
      ) : null}
      <TabText>{text}</TabText>
    </StyledTab>
  );
};

const Tab: FC<ITabProps> = ({ onTabSelection, children, selected }) => (
  <StyledTabs>
    {React.Children.map(children, (child, index) =>
      React.cloneElement(child as ReactElement, {
        onClick: () => onTabSelection && onTabSelection(index),
        isSelected: selected === index,
      })
    )}
  </StyledTabs>
);

export default Tab;
